import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiPhone, FiMail, FiMapPin, FiFacebook, FiTwitter, FiInstagram, FiLinkedin, FiAward } = FiIcons;

const Footer = () => {
  useEffect(() => {
    // Load CQC widget script
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://www.cqc.org.uk/sites/all/modules/custom/cqc_widget/widget.js?data-id=1-7077360945&data-host=https://www.cqc.org.uk&type=location';
    script.async = true;
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-900 to-secondary-900 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-lg">O</span>
              </div>
              <div>
                <h3 className="text-xl font-bold">Odara Care</h3>
                <p className="text-sm text-gray-400">Community Centred Care</p>
              </div>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">
              Bringing warmth, compassion, and professional care to families across our community. Your home is where your heart is, and we're here to help you stay.
            </p>
            
            <div className="py-4">
              <div className="flex items-center space-x-2 mb-3">
                <SafeIcon icon={FiAward} className="w-5 h-5 text-primary-400" />
                <span className="text-sm font-medium text-gray-300">CQC Regulated</span>
              </div>
              <div id="cqc-widget-container" className="bg-white/10 p-3 rounded-lg">
                <div className="text-xs text-gray-400 mb-2">Care Quality Commission Rating:</div>
                <div className="text-sm text-primary-400">Loading CQC rating...</div>
              </div>
            </div>

            <div className="flex space-x-4">
              <SafeIcon icon={FiFacebook} className="w-5 h-5 text-gray-400 hover:text-primary-400 cursor-pointer transition-colors" />
              <SafeIcon icon={FiTwitter} className="w-5 h-5 text-gray-400 hover:text-primary-400 cursor-pointer transition-colors" />
              <SafeIcon icon={FiInstagram} className="w-5 h-5 text-gray-400 hover:text-primary-400 cursor-pointer transition-colors" />
              <SafeIcon icon={FiLinkedin} className="w-5 h-5 text-gray-400 hover:text-primary-400 cursor-pointer transition-colors" />
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li><Link to="/about" className="text-gray-300 hover:text-primary-400 transition-colors">About Our Family</Link></li>
              <li><Link to="/services" className="text-gray-300 hover:text-primary-400 transition-colors">Care Services</Link></li>
              <li><Link to="/careers" className="text-gray-300 hover:text-primary-400 transition-colors">Careers & Jobs</Link></li>
              <li><Link to="/blog" className="text-gray-300 hover:text-primary-400 transition-colors">Care Stories</Link></li>
              <li><Link to="/where-we-operate" className="text-gray-300 hover:text-primary-400 transition-colors">Where We Operate</Link></li>
              <li><Link to="/contact" className="text-gray-300 hover:text-primary-400 transition-colors">Contact Us</Link></li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Care We Provide</h4>
            <ul className="space-y-2">
              <li className="text-gray-300">Personal Care at Home</li>
              <li className="text-gray-300">Daily Companionship</li>
              <li className="text-gray-300">Memory Care Support</li>
              <li className="text-gray-300">Family Respite Care</li>
              <li className="text-gray-300">Live-in Care</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Get In Touch</h4>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiPhone} className="w-5 h-5 text-primary-400 mt-0.5" />
                <div>
                  <p className="text-gray-300">Always Available</p>
                  <p className="text-white font-medium">03333 394 077</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiMail} className="w-5 h-5 text-primary-400 mt-0.5" />
                <div>
                  <p className="text-gray-300">Email Us</p>
                  <p className="text-white font-medium">enquiries@odaracare.org.uk</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiMapPin} className="w-5 h-5 text-primary-400 mt-0.5" />
                <div>
                  <p className="text-gray-300">HQ</p>
                  <p className="text-white font-medium">86-90 Paul Street<br />London, EC2A 4NE</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-400 text-sm">
            © 2026 Odara Care. All rights reserved. Trusted by families since 2017.
          </p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <Link to="/privacy" className="text-gray-400 hover:text-primary-400 text-sm transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="text-gray-400 hover:text-primary-400 text-sm transition-colors">Terms of Service</Link>
            <Link to="/cookies" className="text-gray-400 hover:text-primary-400 text-sm transition-colors">Cookie Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
